import { useCallback } from "react"
import { download, generateCsv, mkConfig } from "export-to-csv"
import type { DateTime } from "luxon"

import { toCsvText } from "../../../../../lib/stringLib"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"
import { getHostOrganizerName } from "../../home/components/meetigs/helpers"

import { formatTimeWindow } from "../meetingRoomsLib"
import type { MeetingRoomExportRow } from "../meetingRoomsTypes"

type Params = Readonly<{
    rows: ReadonlyArray<MeetingRoomExportRow>
    date: DateTime
    hourLabel: string
}>

export function useExportMeetingRooms({ rows, date, hourLabel }: Params) {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    return useCallback(() => {
        const roomHeader = l10n.getString("service-center-meeting-rooms-table-room-header")
        const floorHeader = l10n.getString("service-center-meeting-rooms-drawer-floor-label")
        const capacityHeader = l10n.getString("service-center-meeting-rooms-drawer-capacity-label")
        const statusHeader = l10n.getString("service-center-meeting-rooms-table-status-header")
        const bookingHeader = l10n.getString("service-center-meeting-rooms-table-booking-header")
        const timeHeader = l10n.getString("service-center-meeting-rooms-table-time-header")
        const ownerHeader = l10n.getString("service-center-meeting-rooms-drawer-owner-subheading")
        const participantsHeader = l10n.getString("service-center-meeting-rooms-drawer-participants-label")

        const csvOptions = mkConfig({
            fieldSeparator: ";",
            quoteStrings: true,
            useBom: true,
            columnHeaders: [
                roomHeader,
                floorHeader,
                capacityHeader,
                statusHeader,
                bookingHeader,
                timeHeader,
                ownerHeader,
                participantsHeader,
            ],
            filename: `Facilitynet-meeting-rooms-${dateTimeFactory.formatDate(date)}`,
        })

        const toStatusText = ({ status, item }: MeetingRoomExportRow) => {
            if (status) {
                // Fluent wraps variables in bidi isolation marks, which would end up as stray characters in the file.
                return l10n
                    .getStringForEnum("service-center-meeting-rooms-status", status, status, { hour: hourLabel })
                    .replace(/[\u2068\u2069]/g, "")
            }
            return item
                ? l10n.getStringForEnum("service-center-meeting-rooms-booking-state", item.state, item.state)
                : ""
        }

        const exportRows = rows.map((row) => ({
            [roomHeader]: toCsvText(row.room.name),
            [floorHeader]: toCsvText(row.room.floorName),
            [capacityHeader]: row.room.seats ?? "",
            [statusHeader]: toStatusText(row),
            [bookingHeader]: toCsvText(row.item?.booking.meetingName ?? ""),
            [timeHeader]: row.item ? formatTimeWindow(dateTimeFactory, row.item.timeRange) : "",
            [ownerHeader]: toCsvText((row.item && getHostOrganizerName([...row.item.booking.organizers])) ?? ""),
            [participantsHeader]: row.item?.booking.participants ?? "",
        }))

        download(csvOptions)(generateCsv(csvOptions)(exportRows))
    }, [rows, date, hourLabel, dateTimeFactory, l10n])
}
